import React, { useEffect, useRef, useState } from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getMapMarkerIcon, MapMarkerIconProps } from 'src/shared/components/map-marker-icon';
import { setHoveredSite, setSelectedSite } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSlice';
import { getPortfolioSiteExplorerState } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSelector';
import { getPortfolioMetricColor, getPortfolioMetricValue } from 'src/app/portfolio/helpers/portfolioHelpers';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { HabitatInsightsMetricType } from 'src/shared/types';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { useMapMarkersQuery, useSitePopupQuery } from '../../state/api/portfolioSiteExplorerGraphSlice';
import usePortfolioSiteExplorerAnalytics from '../../hooks/usePortfolioSiteExplorerAnalytics';
import ViewButton from '../view-button/ViewButton';
import SiteSpeciesRiskModal from '../site-species-risk-modal/SiteSpeciesRiskModal';

type PortfolioSiteExplorerMarkersProps = {
    selectedMetric: string;
    speciesColorDomain?: { min: number; max: number };
};

const PortfolioSiteExplorerMapMarkers = ({ selectedMetric, speciesColorDomain }: PortfolioSiteExplorerMarkersProps) => {
    const dispatch = useAppDispatch();
    const uiState = useAppSelector(getPortfolioSiteExplorerState);
    const { hoveredSiteId, selectedSiteId } = uiState;
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const { currentCustomerId } = useCustomer();
    const { trackSiteMarkerHover, trackSiteClick } = usePortfolioSiteExplorerAnalytics();

    const isSpeciesMetric = selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' || selectedMetric === 'POSSIBLE_INVASIVE_SPECIES';

    const { currentData: markersData } = useMapMarkersQuery(
        {
            customerId: currentCustomerId,
            ...(isSpeciesMetric ? {} : { metric: selectedMetric }),
            ...(selectedFilters.length ? { filters: selectedFilters } : {}),
        },
        { skip: selectedMetric === 'NA' }
    );

    // Keep refs to markers so we can trigger click programmatically
    const markerRefs = useRef<Record<string, L.Marker | null>>({});

    const handleSiteClick = (siteId: string) => {
        if (selectedSiteId !== siteId) {
            trackSiteClick(siteId, 'map');
            dispatch(setSelectedSite(siteId));
        }
    };

    const handleSiteHover = (siteId: string, isHovering: boolean) => {
        if (selectedSiteId) {
            return;
        }
        if (isHovering) {
            trackSiteMarkerHover(siteId);
        }
        dispatch(setHoveredSite(isHovering ? siteId : null));
    };

    useEffect(() => {
        if (!selectedSiteId) {
            Object.keys(markerRefs.current).forEach(siteId => {
                const marker = markerRefs.current[siteId];
                if (marker && marker.isPopupOpen()) {
                    marker.closePopup();
                }
            });
            return;
        }
        const marker = markerRefs.current[selectedSiteId];
        try {
            if (marker) {
                if (typeof marker.fire === 'function') {
                    marker.fire('click');
                }
                if (typeof marker.openPopup === 'function') {
                    marker.openPopup();
                }
            }
        } catch (_e) {
            // no-op
        }
    }, [selectedSiteId]);

    return (
        <>
            {(markersData?.markers ?? []).map((marker, index) => {
                const { siteId, coordinates, metricValue, speciesRedListCount, speciesInvasiveCount, hasEdna, hasHabitatInsights } = marker;

                const effectiveMetricValue =
                    selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES'
                        ? speciesRedListCount ?? 0
                        : selectedMetric === 'POSSIBLE_INVASIVE_SPECIES'
                        ? speciesInvasiveCount ?? 0
                        : metricValue ?? 0;

                const color = getPortfolioMetricColor(effectiveMetricValue, selectedMetric, speciesColorDomain);
                const isHovered = hoveredSiteId === siteId;
                const isSelected = selectedSiteId === siteId;
                const opacity = isSelected ? 1 : isHovered ? 0.9 : 1;

                const getIconParams: MapMarkerIconProps = {
                    color: color,
                    groupOrder: index,
                    iconType: 'default',
                };

                const markerEventHandlers = {
                    click: () => handleSiteClick(siteId),
                    mouseover: () => handleSiteHover(siteId, true),
                    mouseout: () => handleSiteHover(siteId, false),
                    popupopen: () => {
                        dispatch(setSelectedSite(siteId));
                    },
                    popupclose: () => {
                        dispatch(setSelectedSite(null));
                    },
                };

                return (
                    <Marker
                        key={siteId}
                        ref={ref => {
                            if (ref) {
                                if (typeof (ref as L.Marker).openPopup === 'function') {
                                    markerRefs.current[siteId] = ref as L.Marker;
                                } else {
                                    const componentRef = ref as { leafletElement?: L.Marker };
                                    markerRefs.current[siteId] = componentRef.leafletElement || null;
                                }
                            } else {
                                markerRefs.current[siteId] = null;
                            }
                        }}
                        position={coordinates as LatLngExpression}
                        icon={getMapMarkerIcon(getIconParams)}
                        eventHandlers={markerEventHandlers}
                        opacity={opacity}
                        riseOnHover={true}
                    >
                        <Popup autoPan={true} offset={[0, 2]}>
                            <SiteDetailsContent siteId={siteId} hasEdnaData={hasEdna} hasHabitatInsightsData={hasHabitatInsights} />
                        </Popup>

                        {isHovered && !selectedSiteId && (
                            <Tooltip
                                direction='top'
                                offset={[0, -6]}
                                opacity={1}
                                className='font-poppins rounded-[15px] p-4 shadow-lg max-w-[350px] bg-white'
                            >
                                <SiteDetailsContent siteId={siteId} hasEdnaData={hasEdna} hasHabitatInsightsData={hasHabitatInsights} />
                            </Tooltip>
                        )}
                    </Marker>
                );
            })}
        </>
    );
};

// Detailed site information component for both tooltip and popup
const SiteDetailsContent = ({
    siteId,
    hasEdnaData,
    hasHabitatInsightsData,
}: {
    siteId: string;
    hasEdnaData: boolean;
    hasHabitatInsightsData: boolean;
}) => {
    const { currentCustomerId } = useCustomer();
    const { trackSpeciesRiskModalOpen } = usePortfolioSiteExplorerAnalytics();
    const [isModalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<'iucn' | 'invasives'>('iucn');

    const { currentData: popupSiteData } = useSitePopupQuery({
        customerId: currentCustomerId,
        siteId: siteId,
    });

    const labelClassName = 'text-grey-80 w-[220px] font-semibold';
    const valueClassName = 'flex-1 text-grey-80 ml-2';
    const rowClassName = 'flex justify-between items-center';

    return (
        <div className=' min-w-[300px] max-w-[400px]'>
            <div className='flex flex-col gap-2 m-0 mb-2  border-b border-grey-20 pb-2 text-sm '>
                <h4 className=' text-teal-100'>{popupSiteData?.name}</h4>
                <div className=' text-black'>{popupSiteData?.latestYear}</div>
            </div>
            <div className='mb-3 border-b border-grey-20 pb-3 text-xs flex flex-col gap-2'>
                <div className={rowClassName}>
                    <span className={labelClassName}>Area:</span>
                    <span className={valueClassName}>{popupSiteData?.totalArea.toFixed(2)}</span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Country:</span>
                    <span className={valueClassName}>{popupSiteData?.country}</span>
                </div>
                <div className={rowClassName}>
                    <span className={labelClassName}>Combined vegetation cover:</span>
                    <span className={valueClassName}>
                        {getPortfolioMetricValue({
                            siteMetrics: popupSiteData?.metrics,
                            metric: HabitatInsightsMetricType.LAND_COVER,
                        })}
                        %
                    </span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Vegetation health:</span>
                    <span className={valueClassName}>
                        {getPortfolioMetricValue({
                            siteMetrics: popupSiteData?.metrics,
                            metric: HabitatInsightsMetricType.VEGETATION_CONDITION,
                        })}
                    </span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Connectivity:</span>
                    <span className={valueClassName}>
                        {getPortfolioMetricValue({
                            siteMetrics: popupSiteData?.metrics,
                            metric: HabitatInsightsMetricType.ALL_CONNECTIVITY,
                        })}
                    </span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Human Modification (For 2017):</span>
                    <span className={valueClassName}>
                        {getPortfolioMetricValue({
                            siteMetrics: popupSiteData?.metrics,
                            metric: HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX,
                        })}
                    </span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Biodiversity Intactness (For 2020):</span>
                    <span className={valueClassName}>
                        {getPortfolioMetricValue({
                            siteMetrics: popupSiteData?.metrics,
                            metric: HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX,
                        })}
                    </span>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Predicted IUCN Red List species:</span>
                    <button
                        type='button'
                        className={`${valueClassName} text-primary underline cursor-pointer text-left`}
                        onClick={() => {
                            setModalTab('iucn');
                            setModalOpen(true);
                            trackSpeciesRiskModalOpen(siteId, 'iucn', 'tooltip');
                        }}
                    >
                        {popupSiteData?.speciesRedListCount}
                    </button>
                </div>

                <div className={rowClassName}>
                    <span className={labelClassName}>Predicted invasive species:</span>
                    <button
                        type='button'
                        className={`${valueClassName} text-primary hover:underline cursor-pointer text-left`}
                        onClick={() => {
                            setModalTab('invasives');
                            setModalOpen(true);
                            trackSpeciesRiskModalOpen(siteId, 'invasive', 'tooltip');
                        }}
                    >
                        {popupSiteData?.speciesInvasiveCount}
                    </button>
                </div>
            </div>
            <div className='flex items-end justify-end'>
                <ViewButton
                    site={{
                        hasEdna: hasEdnaData,
                        hasHabitatInsights: hasHabitatInsightsData,
                        id: siteId,
                    }}
                    key={siteId}
                    buttonText='View site data'
                    locationForTracking='tooltip'
                />
            </div>
            {isModalOpen && <SiteSpeciesRiskModal onClose={() => setModalOpen(false)} siteId={siteId} initialTab={modalTab} />}
        </div>
    );
};

export default PortfolioSiteExplorerMapMarkers;
